/**
 * Browser entry point.
 *
 * The module-scope work below ran in pages/_app.tsx before, guarded by
 * `typeof window !== 'undefined'` because that module also executed on the server. It is
 * here now, unguarded, because this file only ever runs in a browser — and it runs before
 * the first render rather than during the first module's evaluation, which is what the
 * window error handlers need in order to catch a throw from the very first render.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { installCredentialedFetch } from '@syia-ai/utils/auth';
import { enableColumnResize } from '@syia-ai/ui/column-resize';
import { initClientLogger } from '@/observability/clientLogger';
import { initPostHog } from '@/lib/posthog';
import { initVesselBridge } from '@/lib/vessel-bridge';
import { loadRemoteRegistry } from '@/federation/remoteRegistry';
import { routes } from '@/routes';

// First, and before any render: the window `error` / `unhandledrejection` listeners must
// be attached before the first render can throw. Idempotent.
initClientLogger();

initPostHog();
initVesselBridge();

// Column expand (drag a header edge, double-click to fit) on every data table in the
// shell and every federated App it hosts, including tables inside injected HTML. The
// shared DataTable has it built in and is skipped.
enableColumnResize();

// Send the HttpOnly accessToken cookie on every request to our own origins.
// Safe/no-op locally (no .siya.com cookie there) — the Bearer header still applies.
//
// The shell owns window.fetch under Module Federation, so it governs cross-origin cookie
// sending for EVERY remote App. Same-origin (shell API) is always credentialed. Cross-origin
// is credentialed ONLY for subdomains listed here — the Apps migrated to cookie-only whose
// CORS echoes the origin + `Allow-Credentials: true`. Non-migrated Apps still return
// `Allow-Origin: '*'` (illegal with credentials), so they must NOT be listed until migrated.
// Add a subdomain here as each App flips to cookie-only. Migrated: crew-plan (crew.<host>),
// crew-workhours, and batch 1: vir, psc, tmsa, class, eta. Each listed App MUST be deployed
// with its route-CORS fix (applyCredentialedCors) BEFORE it appears here, or its cross-origin
// calls break (cookie sent -> '*' -> CORS block).
installCredentialedFetch({
  crossOriginSubdomains: [
    'crew', 'crew-workhours', 'vir', 'psc', 'tmsa', 'class', 'eta', // batch 1
    'mr', 'fueleu', 'forms', 'cii', 'sire',                          // batch 2 (sire-cdi-app -> sire.<host>)
    'defect', 'overview', 'data-catalog',                            // batch 3
    'lsa', 'ihm', 'vessel-particulars', 'alcovisor', 'bwt',          // batch 4a
    'fleet-distribution', 'fueloil', 'lubeoil', 'rightship',         // batch 4a (fuel-oil->fueloil, lube-oil->lubeoil)
    'pms', 'purchase', 'pdf-report', 'budget', 'eeoi',               // batch 4b
    'marine-services', 'pdf-converter', 'voyage-monitoring', 'seaverse-fleet', 'seaverse', // batch 4b
    'vessel-issues',                                                 // batch 4c
    // NOTE: qhse-initiation-tracker was extracted to its own repo (syia-ai/QHSE-task-tracker);
    // migrate + add it there, not here — omitted so we don't send it the cookie unverified.
  ],
});

// Start fetching /remotes.json now, in parallel with the session check, rather than when
// the first micro-app route mounts. RegistryRoute awaits this same promise. A failure is
// shown there, with a retry; swallowed here only so it is not ALSO an unhandled rejection.
loadRemoteRegistry().catch(() => {});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

const router = createBrowserRouter(routes);

createRoot(container).render(
  // StrictMode double-invokes effects in development only. It is on deliberately: the
  // ported components came from a codebase where a server render hid single-mount
  // assumptions, and remounting is exactly what a federated route does on navigation.
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
